import React, { useEffect, useState } from 'react'
import { FileUploadProgressProps } from '../../utils/types/fileUploadTypes'
import SvgCross from '../../assets/images/components/cross'
import {
  SvgDownloadIcon,
  SvgTickUser,
  SvgUploadingIcon,
} from '../../assets/images'
import FileTypeComponentIcon from './FileTypeComponentIcon'
import { NAMESPACE, UPLOADING_STATE } from '../../utils'
import ConfirmationModal from '../ConfirmationModal'
import Modal from '../Modal'

const FileUploadProgress: React.FC<FileUploadProgressProps> = ({
  fileName,
  uploadedSize,
  totalSize,
  progress = uploadedSize?.progress ?? 0,
  onCancel,
  index,
  handlePreviewToggle,
  fileType,
  viewMode,
  editMode,
  disableRemove,
  auditHistoryValue,
  disabled,
  isOpenForListing,
  tabIndex,
  isUploadDisabled,
}) => {
  const [singleFileLoaderRef, setSingleFileLoaderRef] = useState(false)
  const [modal, setModal] = useState<boolean>(false)
  const [progressBar, setProgressBar] = useState(progress)

  const trimName = (str: string) => {
    const MAX_SIZE = 15
    const length = str.length
    if (length < MAX_SIZE) return str
    return str.slice(0, MAX_SIZE) + '...'
  }

  useEffect(() => {
    if (progress !== 1) {
      const interval = setInterval(() => {
        setProgressBar((prev) => (prev < 0.9 ? prev + 0.1 : prev))
      }, 300)
      return () => clearInterval(interval)
    } else {
      setProgressBar(progress)
    }
  }, [progress])

  const handleRemove = async () => {
    setSingleFileLoaderRef((prev) => !prev)
    await handlePreviewToggle(
      index,
      fileType,
      NAMESPACE.PREVIEW,
      editMode,
      true,
      fileName
    )
    setSingleFileLoaderRef((prev) => !prev)
  }

  return (
    <div className="relative flex flex-col items-center p-1 bg-gray-50 shadow rounded-md !min-w-[200px]">
      <div className="flex-shrink-0 h-12 w-12">
        {<FileTypeComponentIcon key={index} fileType={fileType} />}
      </div>
      <div className="flex absolute right-0">
        {(viewMode || editMode) && !isOpenForListing ? (
          !singleFileLoaderRef ? (
            <div
              className="flex flex-row-reverse justify-center  pr-2 h-8 w-8 cursor-pointer -mt-2"
              onClick={(e) => {
                e.stopPropagation()
                handleRemove()
              }}
              tabIndex={tabIndex ?? 0}
              role="button"
              onKeyDown={async (e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  setSingleFileLoaderRef((prev) => !prev)
                  await handlePreviewToggle(
                    index,
                    fileType,
                    NAMESPACE.PREVIEW,
                    editMode,
                    true,
                    fileName
                  )
                  setSingleFileLoaderRef((prev) => !prev)
                }
              }}
            >
              <SvgDownloadIcon className="h-full w-full" />
            </div>
          ) : (
            <span className="flex flex-row-reverse justify-center  h-[1.5rem] w-[1.5rem] animate-spin mr-2">
              {<SvgUploadingIcon className="h-full w-full" />}
            </span>
          )
        ) : null}
        {(!viewMode || editMode) &&
          !disableRemove &&
          !singleFileLoaderRef &&
          !disabled &&
          !isUploadDisabled && (
            <div
              className={`text-gray-400 hover:text-red-500 transition-colors pr-2 ${
                progressBar !== 1 && !editMode
                  ? 'cursor-default'
                  : 'cursor-pointer'
              }`}
              onClick={(e) => {
                e.stopPropagation()
                setModal(true)
              }}
              title={NAMESPACE.DELETE}
              tabIndex={tabIndex ?? 0}
              role="button"
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  setModal(true)
                }
              }}
            >
              <SvgCross />
            </div>
          )}
      </div>
      {modal && (
        <div onClick={(e) => e.stopPropagation()}>
          <Modal
            isOpen={modal}
            closeModal={() => setModal(false)}
            showCloseButton={true}
          >
            <ConfirmationModal
              message={NAMESPACE.DELETE_FILE}
              handleNo={() => setModal(false)}
              handleYes={() => {
                setModal(false)
                if (progress === 1 || editMode) {
                  onCancel(index, auditHistoryValue === undefined)
                }
              }}
            />
          </Modal>
        </div>
      )}
      <div className="flex-grow mx-4">
        <div className="group max-w-[17rem] text-left">
          <p
            className={`font-medium text-xs !truncate ${
              auditHistoryValue !== undefined
                ? 'cursor-pointer group-hover:text-blue-600 hover:underline'
                : 'text-black cursor-default'
            }`}
            onClick={(e) => {
              e.stopPropagation()
              if (auditHistoryValue !== undefined)
                handlePreviewToggle(
                  index,
                  fileType,
                  NAMESPACE.PREVIEW,
                  editMode,
                  false,
                  fileName
                )
            }}
          >
            {trimName(fileName)}
          </p>
        </div>
        {(!viewMode && !editMode) || isOpenForListing ? (
          <p className="text-xs text-gray-500 flex flex-row cursor-default">
            {Math.round(progress * (totalSize / 1024))} KB of{' '}
            {Math.round(totalSize / 1024)} KB •
            {progress !== 1 ? (
              <span className="flex items-center align-middle gap-1 pl-2 text-black text-s">
                <span className="animate-spin">{<SvgUploadingIcon />}</span>
                {UPLOADING_STATE.FILE_UPLOADING_STATE}
              </span>
            ) : (
              <span className="flex items-center align-middle gap-1 pl-2 text-black text-s">
                {<SvgTickUser width="11.85" height="11.85" success={true} />}
                {UPLOADING_STATE.FILE_COMPLETED_STATE}
              </span>
            )}
          </p>
        ) : (
          <span className="text-xs text-gray-500 flex flex-row cursor-default">
            {Math.round(totalSize)} KB • {auditHistoryValue ?? ''}
          </span>
        )}

        {((!viewMode && !editMode) || isOpenForListing) && (
          <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
            <div
              className="absolute top-0 left-0 h-2 bg-blue-500 rounded"
              style={{ width: `${progressBar * 100}%` }}
            ></div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUploadProgress
